import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { CajaEmoji } from '#components/mobile/travesia/CajaEmoji';
import {
  cuotaOcasional,
  mensualesVisibles,
  nombreCofre,
  nombreGrupo,
  OCASIONALES,
  seleccionInicial,
} from '#components/mobile/travesia/catalogo';
import type { IdCofre, Perfil } from '#components/mobile/travesia/catalogo';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { useTrato } from '#components/mobile/travesia/trato';
import { Boton } from '#components/mobile/ui/Boton';
import { color } from '#components/mobile/ui/tokens';

import { HojaTravesia, Marca, TituloHoja, useEuros } from './comun';

const PLANES = ['restaurantes', 'ocio', 'suscripciones'];

type HojaCategoriasProps = {
  /** Paso 3 (cada mes) o paso 4 (de vez en cuando). */
  tipo: 'mensual' | 'ocasional';
  perfil: Perfil;
  ocupado?: boolean;
  onCrear: (elegidos: IdCofre[]) => void;
};

/**
 * Pasos 4 y 5 · los cofres, con filas como las del Plan (emoji en su caja de
 * color, lo sugerido y ✓). Al crearlos, el Plan de detrás se llena.
 */
export function HojaCategorias({
  tipo,
  perfil,
  ocupado,
  onCrear,
}: HojaCategoriasProps) {
  const { t } = useTranslation();
  const { eur0 } = useEuros();
  const { contexto } = useTrato();
  const mensual = tipo === 'mensual';
  const lista = mensual ? mensualesVisibles(perfil) : OCASIONALES;
  const [sel, setSel] = useState<IdCofre[]>(() => {
    const ini = seleccionInicial(perfil);
    return mensual ? ini.mensuales : ini.ocasionales;
  });
  const [recien, setRecien] = useState<string | null>(null);
  const [gavi, setGavi] = useState<{
    texto: string | null;
    estado: GaviEstado;
    clave: number;
  }>({ texto: null, estado: 'vuela', clave: 0 });

  function alternar(id: IdCofre) {
    const ya = sel.includes(id);
    const nueva = ya ? sel.filter(x => x !== id) : [...sel, id];
    setSel(nueva);
    setRecien(ya ? null : id);
    let texto: string | null = null;
    let estado: GaviEstado = 'reposo';
    if (mensual && nueva.length === 0) {
      texto = t(
        'Zero expenses? Either you live in a lighthouse or something does not add up. Tick at least one.',
        { context: contexto },
      );
      estado = 'ojos-plato';
    } else if (
      mensual &&
      !ya &&
      PLANES.includes(id) &&
      PLANES.every(x => nueva.includes(x as IdCofre))
    ) {
      texto = t(
        'Restaurants, leisure and series. The plan is to live well. Respect.',
      );
      estado = 'aplaude';
    } else if (!mensual && !ya && id === 'vacaciones') {
      texto = t('Beach paid for since January. That is how it should be.', {
        context: contexto,
      });
      estado = 'aplaude';
    }
    setGavi(g => ({ texto, estado, clave: g.clave + 1 }));
  }

  const grupos = mensual ? (['casa', 'dia'] as const) : (['vez'] as const);
  const pregunta = mensual
    ? t('What does your money go on each month?')
    : t('And now and then?');

  return (
    <HojaTravesia
      aria-label={pregunta}
      data-testid={mensual ? 'hoja-mensuales' : 'hoja-ocasionales'}
      cabeza={
        <Bocadillo
          variante="hoja"
          texto={
            gavi.texto ??
            (mensual
              ? t(
                  'Each category is a chest: you only spend what is inside. Remove the ones you do not need.',
                )
              : t(
                  'Christmas falls on the same day every year and still catches us by surprise.',
                ))
          }
          estado={gavi.estado}
          clave={gavi.clave}
        />
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado || (mensual && sel.length === 0)}
          data-testid="travesia-crear-categorias"
          onPress={() =>
            // En el orden del catálogo, no en el de los toques.
            onCrear(lista.map(c => c.id).filter(id => sel.includes(id)))
          }
        >
          {t('Create categories ({{n}})', { n: sel.length })}
        </Boton>
      }
    >
      <TituloHoja>{pregunta}</TituloHoja>
      {grupos.map(g => (
        <div
          key={g}
          style={{
            marginTop: 10,
            borderRadius: 16,
            overflow: 'hidden',
            backgroundColor: color.surface,
            border: `1px solid ${color.line}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 40,
              padding: '0 16px 0 12px',
              backgroundColor: color.surface2,
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {nombreGrupo(t, g)}
          </div>
          {lista
            .filter(c => c.grupo === g)
            .map(c => {
              const marcado = sel.includes(c.id);
              const sugerido =
                'anual' in c
                  ? t('{{amount}} a year · about {{monthly}}/month', {
                      amount: eur0(c.anual),
                      monthly: eur0(cuotaOcasional(c.anual)),
                    })
                  : t('{{amount}}/month', { amount: eur0(c.sugerido) });
              return (
                <button
                  type="button"
                  key={c.id}
                  role="checkbox"
                  aria-checked={marcado}
                  data-testid={`cofre-${c.id}`}
                  onClick={() => alternar(c.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    minHeight: 60,
                    padding: '8px 12px',
                    width: '100%',
                    textAlign: 'left',
                    boxShadow: `inset 0 1px 0 ${color.line}`,
                    background: 'none',
                    border: 0,
                    color: color.fg,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <CajaEmoji
                    key={recien === c.id ? 'salta' : 'quieta'}
                    emoji={c.emoji}
                    hueco={c.k}
                    salta={recien === c.id}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.25,
                      }}
                    >
                      {nombreCofre(t, c.id)}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: color.fg3,
                      }}
                    >
                      {sugerido}
                    </span>
                  </span>
                  <Marca marcada={marcado} size={26} />
                </button>
              );
            })}
        </div>
      ))}
    </HojaTravesia>
  );
}
